import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './admin/AdminLayout';
import { useAuth } from '../contexts/AuthContext';

// Loading strategy — see docs/adr/ADR-005-no-lazy-loading.md (revised 2026-04-18).
// Fast path (eager): Dashboard + Operations (Bookings, Service Logs, Proposals,
// Service Certificates) + shared infra. These pages and their dependencies land
// in the initial bundle so ~80% of daily workflows have zero navigation latency.
// Everything else is React.lazy — chunk downloads on first click, then cached
// for the session. Suspense fallback lives in AdminLayout so the spinner appears
// inside the content area with the sidebar still visible.

// =============================================================================
// EAGER — Home + Operations actually touched every login
// =============================================================================
// The Dashboard: head office gets the command centre tabs, everyone else today's Dashboard.
import CommandCentre from './admin/pages/CommandCentre';
import BookingsList from './admin/pages/bookings/bookings-list';
import ServiceLogs from './admin/pages/ServiceLogs';
import Proposals from './admin/pages/Proposals';

// =============================================================================
// LAZY — loaded on first navigation to that page
//
// 2026-05-20: moved Estimator, Certification (list), CertificationSubmit,
// BookingSettings, MyAvailability, AppProfile out of the eager set after
// the audit confirmed none are touched every login. CertificationSubmit
// is the only one with a hot path (service-log save → submit cert), so
// service-log-form/SuccessView pre-fetches it on mount — by the time the
// operator clicks "Submit Certificate Now" the chunk is already cached.
// =============================================================================

// Occasional, formerly eager
const Estimator = lazy(() => import('./admin/pages/Estimator'));
const Certification = lazy(() => import('./admin/pages/Certification'));
const CertificationSubmit = lazy(() => import('./admin/pages/certification-submit'));
const BookingSettingsPage = lazy(() => import('./admin/pages/BookingSettings'));
const MyAvailabilityPage = lazy(() => import('./admin/pages/MyAvailability'));
const AppProfile = lazy(() => import('./app/pages/AppProfile'));

// Service log create/edit form is its own route — only loaded when an
// operator clicks "New Service Log" from the list, or edits an existing
// one. 3,800 LOC across 15 files. Same AdminLayout Suspense fallback.
const ServiceLogForm = lazy(() => import('./admin/pages/service-log-form'));

// Accounting
const InvoicesPage = lazy(() => import('./admin/pages/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('./admin/pages/InvoiceDetailPage'));
const ItemsPage = lazy(() => import('./admin/pages/ItemsPage'));
const VendorsPage = lazy(() => import('./admin/pages/VendorsPage'));
const PayoutsPage = lazy(() => import('./admin/pages/PayoutsPage'));
const PayoutHistoryReport = lazy(() => import('./admin/pages/reports/PayoutHistoryReport'));
const VendorStatements = lazy(() => import('./admin/pages/reports/VendorStatements'));
const InvoiceSplits = lazy(() => import('./admin/pages/reports/InvoiceSplits'));
const EquipmentRegister = lazy(() => import('./admin/pages/reports/EquipmentRegister'));

// CRM
const CustomersPage = lazy(() => import('./admin/pages/CustomersPage'));
const CustomerDetailPage = lazy(() => import('./admin/pages/CustomerDetailPage'));
const ChatsInboxPage = lazy(() => import('./admin/pages/ChatsInboxPage'));
const Candidates = lazy(() => import('./admin/pages/Candidates'));
const Leads = lazy(() => import('./admin/pages/Leads'));
const CollaborationsPage = lazy(() => import('./admin/pages/collaborations/CollaborationsPage'));

// Marketing Hub
const MarketingCampaigns = lazy(() => import('./admin/pages/marketing/MarketingCampaigns'));
const MarketingStudio = lazy(() => import('./admin/pages/marketing/MarketingStudio'));
const MarketingSocialPosts = lazy(() => import('./admin/pages/marketing/MarketingSocialPosts'));
const MarketingReputation = lazy(() => import('./admin/pages/marketing/MarketingReputation'));
const MarketingSocialInbox = lazy(() => import('./admin/pages/marketing/MarketingSocialInbox'));
const MarketingSpend = lazy(() => import('./admin/pages/marketing/MarketingSpend'));
const MarketingTrackers = lazy(() => import('./admin/pages/marketing/MarketingTrackers'));
const MarketingLandingPages = lazy(() => import('./admin/pages/marketing/MarketingLandingPages'));

// Rewards
const RewardsDashboard = lazy(() => import('./admin/pages/rewards/RewardsDashboard'));
const RewardsCodes = lazy(() => import('./admin/pages/rewards/RewardsCodes'));
const RewardsRedemptions = lazy(() => import('./admin/pages/rewards/RewardsRedemptions'));
const RewardsVouchers = lazy(() => import('./admin/pages/rewards/RewardsVouchers'));
const VoucherTerms = lazy(() => import('./admin/pages/rewards/VoucherTerms'));
const RewardsCredits = lazy(() => import('./admin/pages/rewards/RewardsCredits'));
const RewardsSettlements = lazy(() => import('./admin/pages/rewards/RewardsSettlements'));
const RewardsPoolLedger = lazy(() => import('./admin/pages/rewards/RewardsPoolLedger'));
const RewardsProgramRules = lazy(() => import('./admin/pages/rewards/RewardsProgramRules'));
const HelpIndex = lazy(() => import('./admin/pages/help/HelpIndex'));
const CodesGuide = lazy(() => import('./admin/pages/help/CodesGuide'));
const TierEngineHelp = lazy(() => import('./admin/pages/help/TierEngineHelp'));
const CardArtworkGuide = lazy(() => import('./admin/pages/help/CardArtworkGuide'));
const PostingRhythmGuide = lazy(() => import('./admin/pages/help/PostingRhythmGuide'));

// Email
const CustomerEmail = lazy(() => import('./admin/pages/CustomerEmail'));
const LeadEmail = lazy(() => import('./admin/pages/LeadEmail'));
const CandidateEmail = lazy(() => import('./admin/pages/CandidateEmail'));
const POSEmails = lazy(() => import('./admin/pages/POSEmails'));

// Training Academy
const TrainingTrainees = lazy(() => import('./admin/pages/TrainingTrainees'));
const TrainingCourses = lazy(() => import('./admin/pages/TrainingCourses'));
const TrainingModules = lazy(() => import('./admin/pages/TrainingModules'));
const TrainingAssessments = lazy(() => import('./admin/pages/TrainingAssessments'));
const TrainingContent = lazy(() => import('./admin/pages/TrainingContentV2'));

// Reports
const TransactionReports = lazy(() => import('./admin/pages/reports/TransactionReports'));
const SalesDashboard = lazy(() => import('./admin/pages/reports/SalesDashboard'));
const CustomerSurvey = lazy(() => import('./admin/pages/reports/CustomerSurvey'));
const LeadPerformance = lazy(() => import('./admin/pages/reports/LeadPerformance'));
const WhatsAppReports = lazy(() => import('./admin/pages/reports/WhatsAppReports'));

// Resources
const ContentLibraryBrowse = lazy(() => import('./admin/pages/ContentLibraryBrowse'));
const ContentLibrarySubmit = lazy(() => import('./admin/pages/ContentLibrarySubmit'));
const ContentLibraryReview = lazy(() => import('./admin/pages/ContentLibraryReview'));

// Peace of Sleep
const POSProperties = lazy(() => import('./admin/pages/pos-properties/POSProperties'));
const POSAmbassadors = lazy(() => import('./admin/pages/POSAmbassadors'));
const Charters = lazy(() => import('./admin/pages/Charters'));
const POSProposals = lazy(() => import('./admin/pages/POSProposals'));
const POSRooms = lazy(() => import('./admin/pages/POSRooms'));
const POSLeads = lazy(() => import('./admin/pages/POSLeads'));

// Administration
const UsersPage = lazy(() => import('./admin/pages/users'));
const TerritoriesPage = lazy(() => import('./admin/pages/TerritoriesPage'));
const TerritoryDetailPage = lazy(() => import('./admin/pages/territory-detail'));
const SplitCodesPage = lazy(() => import('./admin/pages/Splits'));
const ContractsPage = lazy(() => import('./admin/pages/ContractsPage'));
const EquipmentPage = lazy(() => import('./admin/pages/Equipment'));
const LicensingRegionsPage = lazy(() => import('./admin/pages/LicensingRegions'));
const SystemEmailsPage = lazy(() => import('./admin/pages/system-emails'));
const SystemComms = lazy(() => import('./admin/pages/SystemComms'));

// Super Admin
const PermissionProfilesPage = lazy(() => import('./admin/pages/PermissionProfiles'));
const PlatformConfigPage = lazy(() => import('./admin/pages/PlatformConfigPage'));
const TerritoryPurchases = lazy(() => import('./admin/pages/TerritoryPurchases'));
const SystemRevenuePage = lazy(() => import('./admin/pages/SystemRevenuePage'));
const AskAdminPage = lazy(() => import('./admin/pages/AskAdminPage'));
const WhatsAppPage = lazy(() => import('./admin/pages/WhatsAppPage'));

// Dr Cleanstein (Ask)
const AskPage = lazy(() => import('./admin/pages/AskPage'));

// Operator
const OperatorHome = lazy(() => import('./admin/pages/OperatorHome'));

// To Do — the task engine's working screen. Its own page while it earns trust; the
// intention is that it becomes the screen everybody lands on.
const ToDoPage = lazy(() => import('./admin/pages/ToDoPage'));

const PORTAL_STORAGE_KEY = 'sentinel_last_portal';

function DashboardOrSelector() {
  const { getPortalAccess } = useAuth();
  const access = getPortalAccess();
  const roleCount = [access.admin, access.pos, access.ambassador].filter(Boolean).length;

  if (roleCount >= 2 && !localStorage.getItem(PORTAL_STORAGE_KEY)) {
    return <Navigate to="/select-portal" replace />;
  }
  return <CommandCentre />;
}

export function PortalRoutes() {
  return (
    <Routes>
      <Route element={<AdminLayout />}>
        <Route path="/" element={<DashboardOrSelector />} />
        <Route path="/to-do" element={<ToDoPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/service-logs" element={<ServiceLogs />} />
        <Route path="/service-logs/new" element={<ServiceLogForm />} />
        <Route path="/service-logs/:id" element={<ServiceLogForm />} />
        <Route path="/service-logs/:id/edit" element={<ServiceLogForm />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        <Route path="/bookings" element={<BookingsList />} />
        <Route path="/booking-settings" element={<BookingSettingsPage />} />
        <Route path="/my-availability" element={<MyAvailabilityPage />} />
        <Route path="/items" element={<ItemsPage />} />
        <Route path="/content-library" element={<ContentLibraryBrowse />} />
        <Route path="/content-library/submit" element={<ContentLibrarySubmit />} />
        <Route path="/content-library/review" element={<ContentLibraryReview />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/territories" element={<TerritoriesPage />} />
        <Route path="/territories/:id" element={<TerritoryDetailPage />} />
        <Route path="/splits" element={<SplitCodesPage />} />
        <Route path="/contracts" element={<ContractsPage />} />
        <Route path="/equipment" element={<EquipmentPage />} />
        <Route path="/regions" element={<LicensingRegionsPage />} />
        <Route path="/territory-purchases" element={<TerritoryPurchases />} />
        <Route path="/vendors" element={<VendorsPage />} />
        <Route path="/payouts" element={<PayoutsPage />} />
        <Route path="/system-revenue" element={<SystemRevenuePage />} />
        <Route path="/ask-admin" element={<AskAdminPage />} />
        <Route path="/whatsapp" element={<WhatsAppPage />} />
        <Route path="/ask" element={<AskPage />} />
        <Route path="/inbox" element={<ChatsInboxPage />} />
        <Route path="/config" element={<PlatformConfigPage />} />
        {/* Marketing Hub */}
        <Route path="/marketing/studio" element={<MarketingStudio />} />
        <Route path="/marketing/campaigns" element={<MarketingCampaigns />} />
        <Route path="/marketing/social-posts" element={<MarketingSocialPosts />} />
        <Route path="/marketing/reputation" element={<MarketingReputation />} />
        <Route path="/marketing/social-inbox" element={<MarketingSocialInbox />} />
        <Route path="/marketing/spend" element={<MarketingSpend />} />
        <Route path="/marketing/trackers" element={<MarketingTrackers />} />
        {/* Performance was retired on 13 Oct 2026: the campaign tracker's Compare tab replaced it. */}
        <Route path="/marketing/performance" element={<Navigate to="/marketing/trackers" replace />} />
        <Route path="/marketing/landing-pages" element={<MarketingLandingPages />} />
        {/* Rewards Module */}
        <Route path="/rewards" element={<RewardsDashboard />} />
        <Route path="/rewards/codes" element={<RewardsCodes />} />
        <Route path="/rewards/redemptions" element={<RewardsRedemptions />} />
        <Route path="/rewards/referrals" element={<Navigate to="/rewards/codes" replace />} />
        <Route path="/rewards/vouchers" element={<RewardsVouchers />} />
        <Route path="/rewards/vouchers/terms" element={<VoucherTerms />} />
        <Route path="/rewards/credits" element={<RewardsCredits />} />
        <Route path="/rewards/settlements" element={<RewardsSettlements />} />
        <Route path="/rewards/pool-ledger" element={<RewardsPoolLedger />} />
        <Route path="/rewards/program-rules" element={<RewardsProgramRules />} />
        {/* Help — open to every signed-in user, no permission gate */}
        <Route path="/help" element={<HelpIndex />} />
        <Route path="/help/codes" element={<CodesGuide />} />
        <Route path="/help/tiers" element={<TierEngineHelp />} />
        <Route path="/help/card-artwork" element={<CardArtworkGuide />} />
        <Route path="/help/posting-rhythm" element={<PostingRhythmGuide />} />
        {/* Candidates */}
        <Route path="/candidates" element={<Candidates />} />
        {/* Brand Collaborations */}
        <Route path="/collaborations" element={<CollaborationsPage />} />
        {/* Leads + Estimator + Proposals */}
        <Route path="/leads" element={<Leads />} />
        <Route path="/estimator" element={<Estimator />} />
        <Route path="/proposals" element={<Proposals />} />
        {/* Training Academy */}
        <Route path="/training-trainees" element={<TrainingTrainees />} />
        <Route path="/training-courses" element={<TrainingCourses />} />
        <Route path="/training-modules" element={<TrainingModules />} />
        <Route path="/training-assessments" element={<TrainingAssessments />} />
        <Route path="/training-content" element={<TrainingContent />} />
        <Route path="/training-content-v2" element={<Navigate to="/training-content" replace />} />
        {/* Certification */}
        <Route path="/certification" element={<Certification />} />
        <Route path="/certification/submit" element={<CertificationSubmit />} />
        {/* Email */}
        <Route path="/email/customers" element={<CustomerEmail />} />
        <Route path="/email/leads" element={<LeadEmail />} />
        <Route path="/email/candidates" element={<CandidateEmail />} />
        <Route path="/email/pos" element={<POSEmails />} />
        {/* Reports */}
        <Route path="/reports/transactions" element={<TransactionReports />} />
        <Route path="/reports/sales-performance" element={<SalesDashboard />} />
        <Route path="/reports/customer-survey" element={<CustomerSurvey />} />
        <Route path="/reports/lead-performance" element={<LeadPerformance />} />
        <Route path="/reports/whatsapp" element={<WhatsAppReports />} />
        <Route path="/reports/payout-history" element={<PayoutHistoryReport />} />
        <Route path="/reports/vendor-statements" element={<VendorStatements />} />
        <Route path="/reports/invoice-splits" element={<InvoiceSplits />} />
        <Route path="/reports/equipment-register" element={<EquipmentRegister />} />
        {/* Peace of Sleep */}
        <Route path="/pos-properties" element={<POSProperties />} />
        <Route path="/pos-ambassadors" element={<POSAmbassadors />} />
        <Route path="/charters" element={<Charters />} />
        {/* Legacy alias — old sidebar slot, kept so bookmarks survive. */}
        <Route path="/pos-charter-suite-users" element={<Navigate to="/charters?tab=suite" replace />} />
        <Route path="/pos-proposals" element={<POSProposals />} />
        <Route path="/pos-rooms" element={<POSRooms />} />
        <Route path="/pos-leads" element={<POSLeads />} />
        {/* System */}
        <Route path="/email/system" element={<SystemEmailsPage />} />
        {/* Legacy redirect — /system-emails moved under the Email group */}
        <Route path="/system-emails" element={<Navigate to="/email/system" replace />} />
        <Route path="/system-comms" element={<SystemComms />} />
        <Route path="/permission-profiles" element={<PermissionProfilesPage />} />
        {/* Operator + Profile */}
        <Route path="/operator" element={<OperatorHome />} />
        <Route path="/profile" element={<AppProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
